import {
  SessionProvider as CoreProvider,
  useSessionContext
} from '@evnttz/session/react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'

import { authPaths } from '@/shared/lib/routing/auth'

import { session as sessionInstance } from './instance'

/**
 * Superfície do `next-auth/react`, sobre o `@evnttz/session`.
 *
 * Existe para a migração não virar reescrita: 22 arquivos importam de
 * `next-auth/react`, e trocar a linha inteira de import apaga o que estava
 * junto — passa em lint e em teste, e quebra no boot. Aqui só o especificador
 * do módulo muda no call site; os símbolos continuam os mesmos.
 */

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated'

export interface SessionIdentity {
  provider?: string
  provider_uid?: string
  /**
   * Dados do provider. `email` aparece nomeado porque o app compara identidade
   * por ele: `users.email` pode ser sintético (doity, brasiljunior), então a
   * comparação real usa `identities[].meta.email`.
   */
  meta?: { email?: string; [key: string]: unknown }
}

export interface SessionUser {
  id?: string | number
  name?: string
  email?: string
  avatar?: string
  custom_avatar?: string
  roles?: Array<Record<string, unknown>>
  identities?: SessionIdentity[]
  [key: string]: unknown
}

export interface Session {
  user?: SessionUser
  [key: string]: unknown
}

interface UseSessionOptions {
  required?: boolean
  onUnauthenticated?: () => void
}

export const useSession = ({
  required = false,
  onUnauthenticated
}: UseSessionOptions = {}) => {
  const core = useSessionContext()

  const handler = useRef(onUnauthenticated)
  handler.current = onUnauthenticated

  useEffect(() => {
    // Só com o estado RESOLVIDO. Reagir durante `loading` mandaria todo F5 e
    // toda aba nova para o login.
    if (!required) return
    if (core.status !== 'unauthenticated') return

    handler.current?.()
  }, [required, core.status])

  const update = useCallback(async () => {
    const updated = await sessionInstance.update()

    return updated ? { user: updated.user as SessionUser } : null
  }, [])

  // Com `required`, o next-auth NUNCA reporta `unauthenticated`: mantém
  // `loading` enquanto redireciona. Guards contam com isso — reportar
  // `unauthenticated` faz a tela renderizar sem sessão, e quem desestrutura
  // `data.user` estoura com null.
  const status: SessionStatus =
    required && core.status === 'unauthenticated'
      ? 'loading'
      : (core.status as SessionStatus)

  return useMemo(
    () => ({
      data:
        core.isAuthenticated && core.user
          ? ({ user: core.user as SessionUser } as Session)
          : null,
      status,
      /** `absent` (nunca entrou) x `expired` (estava dentro e caiu). */
      reason: core.reason,
      update
    }),
    [core.isAuthenticated, core.user, core.reason, status, update]
  )
}

/**
 * Mesma assinatura do `getSession` do next-auth/react.
 *
 * Substituiu também o `getCachedSession`: aquele módulo era um wrapper
 * single-flight sobre esta chamada, criado porque cada interceptor a disparava
 * por requisição e uma rajada virava uma rajada de renovações concorrentes. O
 * `@evnttz/session` resolve isso na origem — o token é leitura síncrona de
 * memória, e a renovação tem promessa única.
 */
export const getSession = async (): Promise<Session | null> => {
  const restored = await sessionInstance.ready()

  return restored ? { user: restored.user as SessionUser } : null
}

export interface Provider {
  id: string
  name: string
  type: string
  signinUrl: string
  callbackUrl: string
}

/**
 * Mesma assinatura do `getProviders` do next-auth/react, no formato indexado
 * que os call sites iteram.
 *
 * A lista vem da api, derivada de quem tem credencial configurada — provider
 * sem credencial não vira botão que falha no clique.
 */
export const getProviders = async (): Promise<Record<string, Provider> | null> => {
  const available = await sessionInstance.oauth.listProviders()

  if (!available.length) return null

  return Object.fromEntries(
    available.map(id => [
      id,
      {
        id,
        name: id,
        type: 'oauth',
        signinUrl: sessionInstance.oauth.buildAuthorizeUrl({ provider: id }),
        callbackUrl: ''
      }
    ])
  )
}

interface SignInOptions {
  callbackUrl?: string
  redirect?: boolean
  [credential: string]: unknown
}

const currentUrl = () =>
  typeof window === 'undefined' ? '/' : window.location.href

/** Nomes de provider do app, traduzidos para os que a api conhece. */
const PROVIDER_ALIAS: Record<string, string> = {
  bjid: 'brasiljunior'
}

const CREDENTIALS_PROVIDERS = ['credentials', 'domain-sign-in']

export const signIn = async (
  provider?: string,
  options: SignInOptions = {}
) => {
  const { callbackUrl = currentUrl(), redirect = true, ...credentials } = options

  /**
   * A WebView entrega um access token que o app nativo já possui, em vez de
   * credencial. Era o provider `refresh-session` do next-auth; no broker é
   * `POST /auth/login/token`, que reaproveita o token e só acrescenta o cookie
   * de refresh que o fluxo de browser precisa.
   */
  if (provider === 'refresh-session') {
    try {
      await sessionInstance.signInWithToken(String(credentials.token ?? ''))

      if (redirect) window.location.assign(callbackUrl)

      return { error: null, status: 200, ok: true, url: callbackUrl }
    } catch (error) {
      return {
        error: (error as Error).message || 'invalid_grant',
        status: (error as { status?: number }).status ?? 0,
        ok: false,
        url: null
      }
    }
  }

  /**
   * Token que o parceiro injeta no iframe. Era um CredentialsProvider do
   * next-auth; no broker é a asserção — o mesmo caminho do login social, com o
   * token do parceiro no lugar da credencial do IdP.
   */
  if (provider === 'partner-provider') {
    try {
      await sessionInstance.signInWithAssertion({
        provider: String(credentials.provider ?? ''),
        assertion: String(credentials.token ?? '')
      })

      if (redirect) window.location.assign(callbackUrl)

      return { error: null, status: 200, ok: true, url: callbackUrl }
    } catch (error) {
      return {
        error: (error as Error).message || 'invalid_grant',
        status: (error as { status?: number }).status ?? 0,
        ok: false,
        url: null
      }
    }
  }

  if (
    (provider && CREDENTIALS_PROVIDERS.includes(provider)) ||
    (!provider && credentials.login)
  ) {
    try {
      await sessionInstance.signIn({
        login: String(credentials.login ?? credentials.email ?? ''),
        password: String(credentials.password ?? '')
      })

      if (redirect) {
        window.location.assign(callbackUrl)

        return undefined
      }

      return { error: null, status: 200, ok: true, url: callbackUrl }
    } catch (error) {
      return {
        error: (error as Error).message || 'invalid_grant',
        status: (error as { status?: number }).status ?? 0,
        ok: false,
        url: null
      }
    }
  }

  if (!provider) {
    // A rota de login é escopada por tenant — `authPaths` é quem sabe montá-la.
    // Um caminho fixo aqui levaria o participante para fora do evento dele.
    window.location.assign(authPaths.signIn({ callbackUrl }))

    return undefined
  }

  sessionInstance.oauth.authorize({
    provider: PROVIDER_ALIAS[provider] ?? provider,
    intent: 'login',
    returnTo: callbackUrl
  })

  return undefined
}

export const signOut = async (
  options: { callbackUrl?: string; redirect?: boolean } = {}
) => {
  const { callbackUrl = currentUrl(), redirect = true } = options

  await sessionInstance.signOut()

  if (redirect && typeof window !== 'undefined') window.location.assign(callbackUrl)

  return { url: callbackUrl }
}

interface SessionProviderProps {
  children: ReactNode
  /**
   * Aceitas por compatibilidade e ignoradas: sem BFF não há sessão resolvida no
   * servidor para injetar, e a renovação acontece por 401 e por anúncio entre
   * abas — não por intervalo nem por foco de janela.
   */
  session?: Session | null
  refetchInterval?: number
  refetchOnWindowFocus?: boolean
}

export const SessionProvider = ({ children }: SessionProviderProps) => (
  <CoreProvider session={sessionInstance}>{children}</CoreProvider>
)
